import { isInWorkspace } from "@/components/helpers/workspace/permissions";
import ExistedProgramComponentErrModal from "@/components/pieces/coaching/ExistedProgramComponentErrModal";
import { ErrorModal } from "@/components/pieces/schedule/ErrorModal";
import { SUPPORT_EMAIL } from "@/css/constants";
import { CreateCoachMeeting, CreateMeetingStatus } from "@/graphql";

function getBalanceErrorMessage() {
  return !isInWorkspace()
    ? "Oops - you don't have enough credits to schedule this meeting. Please purchase extra credits or choose a different option."
    : "Oops - you don't have enough credits to schedule this meeting. Please request additional credits from your workspace admin.";
}

const errorMessageMap: Partial<
  Record<CreateMeetingStatus | "default", string>
> = {
  [CreateMeetingStatus.WorkspaceBalanceNotEnough]:
    "Whoops! It looks like there is an issue with your workspace's billing. Please contact your workspace admin and let them know the account is out of credits.",
  [CreateMeetingStatus.CoachNotAvailable]:
    "Your selected coach is not currently available for new meetings. Please contact your coach or choose another coach.",
  [CreateMeetingStatus.RequiredFieldIsMissing]:
    "Please fill in the required field in the Provide Additional Information",
  [CreateMeetingStatus.MeetingOverlap]:
    "Unfortunately, the timeslot you selected is no longer available. Please refresh and select another time.",
  [CreateMeetingStatus.ExceedingMaximumCreditPrice]: `You cannot buy this product because its price exceeds your workspace group's maximum credit price config. Please contact your workspace admin.`,
  [CreateMeetingStatus.AlreadyHaveIntro]:
    "You already have an intro meeting with this coach. Please choose another product",
  [CreateMeetingStatus.MaxIntroSlotReached]:
    "You have reached the maximum number of one-on-one coaching intro sessions",
  [CreateMeetingStatus.ComponentNotOngoing]:
    "The one-on-one coaching session is not ongoing.",
  [CreateMeetingStatus.NotInProgramComponentTimeRange]:
    "The one-on-one coaching session is not in the program component time range.",
  [CreateMeetingStatus.RequiredProgramComponent]:
    "You're currently enrolled in a coaching program. To ensure your sessions count toward your program progress, please book through your program dashboard.",
  [CreateMeetingStatus.OneOneMeetingInvalid]:
    "You're currently enrolled in a coaching program. Your selected coach, product, or time slot isn't valid for one-on-one meetings under your current coaching program. Please book your session through the program dashboard.",
  default: `We were unable to schedule this meeting. Please refresh and try again. If this message persists, please contact our support team at ${SUPPORT_EMAIL}.`,
};

function getOtherErrorMessage(error?: CreateMeetingStatus) {
  return error ? errorMessageMap[error] || errorMessageMap["default"] : "";
}

interface ErrorContainerProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  error?: CreateMeetingStatus;
  activePrograms?: CreateCoachMeeting["activePrograms"];
}

export function ErrorContainer({
  visible,
  onCancel,
  onConfirm,
  error,
  activePrograms = [],
}: ErrorContainerProps) {
  const errorMsg =
    error === CreateMeetingStatus.BalanceNotEnough
      ? getBalanceErrorMessage()
      : getOtherErrorMessage(error);
  const isBuyCreditRedirect =
    !isInWorkspace() && error == CreateMeetingStatus.BalanceNotEnough;
  const isOneOneMeetingInvalid =
    error === CreateMeetingStatus.OneOneMeetingInvalid;
  const isRequiredProgramComponent =
    error === CreateMeetingStatus.RequiredProgramComponent;

  if (isRequiredProgramComponent || isOneOneMeetingInvalid) {
    return (
      <ExistedProgramComponentErrModal
        isOpen={visible}
        onClose={onCancel}
        activePrograms={activePrograms}
        errorText={errorMsg}
      />
    );
  }

  return (
    <ErrorModal
      visible={visible}
      onCancel={onCancel}
      onConfirm={onConfirm}
      isBuyCreditRedirect={isBuyCreditRedirect}
      errorMsg={errorMsg}
    />
  );
}
